import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Container, Row, Col, Button, Card, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { DonationFormData, CountryOption, StateOption } from '../../types/donation';
import { donationService } from '../../services/api';
import './DonationForm.css';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

// Luhn algorithm check for credit card numbers
const luhnCheck = (num: string): boolean => {
  const digits = (num || '').replace(/\D/g, '');
  let sum = 0;
  let shouldDouble = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = parseInt(digits.charAt(i), 10);
    if (Number.isNaN(d)) return false;
    if (shouldDouble) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    shouldDouble = !shouldDouble;
  }
  return digits.length > 0 && sum % 10 === 0;
};

interface DonationFormProps {
  entity?: string;
  emailAppName?: string;
  emailTemplateName?: string;
  title?: string;
}

const DonationForm: React.FC<DonationFormProps> = ({
  entity = 'RMS',
  emailAppName = 'Donation',
  emailTemplateName = 'RMSDonation',
  title,
}) => {
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [states, setStates] = useState<StateOption[]>([]);
  const [donationTypes, setDonationTypes] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submissionStatus, setSubmissionStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [defaultCountryId, setDefaultCountryId] = useState<string>('');
  const [defaultStateId, setDefaultStateId] = useState<string>('');
  // reCAPTCHA state
  const [recaptchaToken, setRecaptchaToken] = useState<string>('');
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState<number | null>(null);
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  // Brand-specific assets for overlays and visual cues (local SVG)
  const DEFAULT_LOGO = '';
  const RMS_LOGO = 'https://www.radhamadhavsociety.org/assets/images/about/i-logo-2.png';
  const HYC_LOGO = 'https://harmonyyouthcenter.org/Content/images/footer.png';
  const brandLogoSrc = entity === 'HYC' ? HYC_LOGO : (entity === 'RMS' ? RMS_LOGO : DEFAULT_LOGO);
  const brandAlt = entity === 'HYC' ? 'Harmony Youth Center' : 'Radha Madhav Society';

  if (!RECAPTCHA_SITE_KEY && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('RECAPTCHA_SITE_KEY is not set. Define REACT_APP_RECAPTCHA_SITE_KEY in your .env file.');
  }

  // Load and render Google reCAPTCHA v2 (checkbox) widget
  useEffect(() => {
    const ensureScript = (): Promise<void> => {
      return new Promise((resolve) => {
        const w = window as any;
        if (w.grecaptcha) return resolve();
        const existing = document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]');
        if (existing) {
          existing.addEventListener('load', () => resolve());
          return;
        }
        const s = document.createElement('script');
        s.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        document.body.appendChild(s);
      });
    };

    const renderWidget = () => {
      const w = window as any;
      const el = document.getElementById('recaptcha-container');
      if (!el) return;
      if (recaptchaWidgetId !== null) return; // already rendered

      // Always use standard grecaptcha for v2 checkbox
      const pollUntilReady = () => {
        const api = (w && w.grecaptcha) ? w.grecaptcha : null;
        if (!api) {
          // Script not yet attached to window
          setTimeout(pollUntilReady, 150);
          return;
        }
        const doRender = () => {
          if (recaptchaWidgetId !== null) return;
          if (typeof api.render !== 'function') {
            // Not ready yet; try again shortly
            setTimeout(doRender, 150);
            return;
          }
          try {
            const id = api.render(el, {
              sitekey: RECAPTCHA_SITE_KEY,
              callback: (token: string) => setRecaptchaToken(token),
              'expired-callback': () => setRecaptchaToken(''),
              'error-callback': () => setRecaptchaToken(''),
            });
            setRecaptchaWidgetId(id);
          } catch (e) {
            // In case of intermittent readiness issues
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.warn('reCAPTCHA render failed, retrying...', e);
            }
            setTimeout(doRender, 200);
          }
        };

        if (typeof api.ready === 'function') {
          api.ready(doRender);
        } else {
          // Fallback if ready() is not available
          setTimeout(doRender, 0);
        }
      };

      pollUntilReady();
    };

    let mounted = true;
    ensureScript().then(() => {
      if (!mounted) return;
      // Defer a tick to ensure the container is mounted in DOM
      setTimeout(renderWidget, 0);
    });
    return () => {
      mounted = false;
    };
  }, [RECAPTCHA_SITE_KEY, recaptchaWidgetId]);

  // Date helpers for recurring donations
  const formatDate = (d: Date) => d.toISOString().slice(0, 10);
  const today = new Date();
  // Use local noon to avoid timezone shifts on iOS when converting to ISO
  today.setHours(12, 0, 0, 0);
  const todayStr = formatDate(today);
  const oneYearLaterInit = new Date(today);
  oneYearLaterInit.setFullYear(today.getFullYear() + 1);
  const oneYearLaterStr = formatDate(oneYearLaterInit);
  const formatMMDDYYYY = (iso: string) => {
    if (!iso) return '';
    const parts = iso.split('-');
    if (parts.length !== 3) return iso;
    const [y, m, d] = parts;
    return `${m}/${d}/${y}`;
  };

  // Initial form values
  const initialValues: DonationFormData = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNo: '',
    address: '',
    country: defaultCountryId || '',
    state: defaultStateId || '',
    city: '',
    postalCode: '',
    purpose: '',
    paymentPurpose: '',
    donationAmount: '',
    donationType: 'OneTime',
    paymentMethod: 'CreditCard',
    cardNumber: '',
    cvv: '',
    expirationDate: '',
    recurringStartDate: todayStr,
    recurringEndDate: oneYearLaterStr,
    subscriptionEndType: 'NoEndDate',
    numberOfTerms: '12',
    // ACH defaults
    achRoutingNumber: '',
    achAccountNumber: '',
    achBankName: '',
    achAccountType: 'Checking'
  };

  // Form validation schema
  const validationSchema = Yup.object({
    firstName: Yup.string().max(50, 'First Name must be at most 50 characters').required('First Name is required'),
    lastName: Yup.string().max(50, 'Last Name must be at most 50 characters').required('Last Name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    phoneNo: Yup.string()
      .required('Phone Number is required')
      .test('phone-digits', 'Enter a valid phone number (10 to 15 digits)', (val: string | undefined) => {
        const digits = (val || '').replace(/\D/g, '');
        return digits.length >= 10 && digits.length <= 15;
      }),
    address: Yup.string().max(60, 'Address must be at most 60 characters').required('Address is required'),
    country: Yup.string().required('Country is required'),
    state: Yup.string().required('State is required'),
    city: Yup.string().max(40, 'City must be at most 40 characters').required('City is required'),
    postalCode: Yup.string()
      .matches(/^\d{5}(?:-\d{4})?$/, 'Invalid US ZIP code')
      .required('Postal Code/ZIP is required'),
    // Comments (optional)
    purpose: Yup.string(),
    paymentPurpose: Yup.string().required('Payment Purpose is required'),
    donationAmount: Yup.string()
      .matches(/^\d{1,7}(?:\.\d{1,2})?$/, 'Donation must be a decimal number up to $1,000,000.00')
      .test('amount-range', 'Donation must be a decimal number up to $1,000,000.00', (val) => {
        if (!val) return false;
        const num = Number(val);
        if (!Number.isFinite(num)) return false;
        // strictly greater than 0 and not exceeding 1,000,000.00
        return num > 0 && num <= 1000000;
      })
      .required('Donation Amount is required'),
    donationType: Yup.string().required('Donation Type is required'),
    // Conditional card validations
    cardNumber: Yup.string().when('paymentMethod', (paymentMethod: any, schema: any) =>
      paymentMethod === 'CreditCard'
        ? schema
            .required('Card Number is required')
            .test(
              'card-luhn-and-length',
              'Enter a valid card number (13–19 digits)',
              (val: string) => {
                const digits = (val || '').replace(/\D/g, '');
                if (!/^\d{13,19}$/.test(digits)) return false;
                return luhnCheck(digits);
              }
            )
        : schema.notRequired()
    ),
    cvv: Yup.string().when('paymentMethod', (paymentMethod: any, schema: any) =>
      paymentMethod === 'CreditCard'
        ? schema
            .required('CVV/CVC is required')
            .matches(/^[0-9]{3,4}$/, 'CVV must be 3 or 4 digits')
        : schema.notRequired()
    ),
    expirationDate: Yup.string().when('paymentMethod', (paymentMethod: any, schema: any) =>
      paymentMethod === 'CreditCard'
        ? schema
            .required('Expiration Date is required')
            .matches(/^(0[1-9]|1[0-2])\/([0-9]{2})$/, 'Format must be MM/YY')
            .test('exp-not-past', 'Card has expired', (val: string) => {
              if (!val) return false;
              const match = val.match(/^(0[1-9]|1[0-2])\/([0-9]{2})$/);
              if (!match) return false;
              const month = parseInt(match[1], 10);
              const year = parseInt(match[2], 10);
              const now = new Date();
              const currentMonth = now.getMonth() + 1; // 1-12
              const currentYear = now.getFullYear() % 100; // Get last 2 digits
              return year > currentYear || (year === currentYear && month >= currentMonth);
            })
        : schema.notRequired()
    ),

    // Recurring date validations
    recurringStartDate: Yup.string().when('donationType', (donType: any, schema: any) =>
      donType === 'Recurring'
        ? schema
            .required('Start Date is required')
            .matches(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD format')
            .test('start-not-past', 'Start Date cannot be in the past', (val: string) => {
              if (!val) return false;
              return val >= todayStr;
            })
        : schema.notRequired()
    ),
    recurringEndDate: Yup.string().when(['donationType', 'subscriptionEndType'], function (this: any, [donType, endType]: any[], schema: any) {
      // Only required when donationType is Recurring AND subscriptionEndType is EndsOnDate
      if (donType === 'Recurring' && endType === 'EndsOnDate') {
        return schema
          .required('End Date is required')
          .matches(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD format')
          .test('end-min-2-months', 'End Date must be at least 2 months after Start Date', function (this: any, val: string) {
            const start = (this.parent as any)?.recurringStartDate as string;
            if (!val || !start) return false;
            const startDate = new Date(start + 'T12:00:00');
            const endDate = new Date(val + 'T12:00:00');
            const minEndDate = new Date(startDate);
            minEndDate.setMonth(minEndDate.getMonth() + 2);
            return endDate >= minEndDate;
          });
      }
      return schema.notRequired();
    }),
    subscriptionEndType: Yup.string().when('donationType', (donType: any, schema: any) =>
      donType === 'Recurring'
        ? schema.oneOf(['NoEndDate', 'EndsOnDate', 'EndsAfterTerms']).required('Subscription End Type is required')
        : schema.notRequired()
    ),
    numberOfTerms: Yup.string().when(['donationType', 'subscriptionEndType'], function (this: any, [donType, endType]: any[], schema: any) {
      if (donType === 'Recurring' && endType === 'EndsAfterTerms') {
        return schema
          .required('Number of Terms is required')
          .matches(/^\d+$/, 'Must be a positive number')
          .test('min-terms', 'Minimum 1 term required', (val: string) => parseInt(val, 10) >= 1)
          .test('max-terms', 'Maximum 9999 terms allowed', (val: string) => parseInt(val, 10) <= 9999);
      }
      return schema.notRequired();
    }),

    // ACH validations (only when ACH selected)
    achRoutingNumber: Yup.string().when('paymentMethod', (paymentMethod: any, schema: any) =>
      paymentMethod === 'ACH'
        ? schema
            .required('Routing Number is required')
            .matches(/^\d{9}$/, 'Routing Number must be 9 digits')
        : schema.notRequired()
    ),
    achAccountNumber: Yup.string().when('paymentMethod', (paymentMethod: any, schema: any) =>
      paymentMethod === 'ACH'
        ? schema
            .required('Account Number is required')
            .matches(/^\d{4,17}$/, 'Account Number must be 4–17 digits')
        : schema.notRequired()
    ),
    achBankName: Yup.string().when('paymentMethod', (paymentMethod: any, schema: any) =>
      paymentMethod === 'ACH' ? schema.required('Bank Name is required') : schema.notRequired()
    ),
    achAccountType: Yup.mixed().when('paymentMethod', (paymentMethod: any, schema: any) =>
      paymentMethod === 'ACH'
        ? schema.oneOf(['Checking', 'Savings']).required('Account Type is required')
        : schema.notRequired()
    ),
  });







  // Fetch initial data on component mount
  useEffect(() => {
    let isMounted = true;
    let hasFetched = false;
    
    const fetchInitialData = async () => {
      // Prevent duplicate calls
      if (hasFetched) return;
      hasFetched = true;
      
      try {
        console.log('Fetching initial data for entity:', entity);
        
        // Fetch countries
        const countryData = await donationService.getCountries();
        if (!isMounted) return;
        setCountries(countryData);
        
        // Determine USA as default if available
        const usa = countryData.find(c =>
          /^(united states|usa|united states of america)$/i.test(c.name)
        );
        const fallback = countryData[0];
        const selected = usa || fallback;
        if (selected) {
          setDefaultCountryId(selected.value);
          // Always fetch states by country ID (per requirement)
          const countryIdForStates = Number.parseInt(String(selected.value), 10) || Number(selected.id);
          const defaultStates = await donationService.getStatesByCountry(countryIdForStates);
          if (!isMounted) return;
          setStates(defaultStates);
          // Preselect Georgia if available
          const ga = defaultStates.find((s) => /^georgia$/i.test(s.name));
          if (ga) {
            setDefaultStateId(ga.value);
          } else if (defaultStates.length) {
            setDefaultStateId(defaultStates[0].value);
          }
        }
        
        // Fetch donation types
        const types = await donationService.getDonationTypes(entity === 'HYC' ? 'HYC' : 'RMS');
        if (!isMounted) return;
        setDonationTypes(types);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };
    
    fetchInitialData();
    
    return () => {
      isMounted = false;
    };
  }, [entity]);

  // Fetch states when country changes
  const handleCountryChange = async (selectedCountry: any, formikProps: any) => {
    if (selectedCountry && selectedCountry.value) {
      formikProps.setFieldValue('country', selectedCountry.value);
      formikProps.setFieldValue('state', '');
      
      // Fetch states by selected country's ID
      const stateData = await donationService.getStatesByCountry(Number(selectedCountry.value));
      setStates(stateData);
      // Default to Georgia if present, else first state
      const ga = stateData.find((s) => /^georgia$/i.test(s.name));
      const chosen = ga || stateData[0];
      if (chosen) {
        formikProps.setFieldValue('state', String(chosen.id));
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (values: DonationFormData, formikHelpers?: any) => {
    setLoading(true);
    setSubmissionStatus(null);
    // Require a valid reCAPTCHA token before submitting
    if (!recaptchaToken) {
      setLoading(false);
      setSubmissionStatus({ success: false, message: 'Please verify reCAPTCHA before submitting.' });
      return;
    }
    // Server-side verify the token
    const recaptchaResult = await donationService.verifyRecaptcha(recaptchaToken);
    if (!recaptchaResult || recaptchaResult.success !== true) {
      setLoading(false);
      setSubmissionStatus({ success: false, message: 'reCAPTCHA verification failed. Please try again.' });
      try {
        const w = window as any;
        if (recaptchaWidgetId !== null && w.grecaptcha) {
          w.grecaptcha.reset(recaptchaWidgetId);
          setRecaptchaToken('');
        }
      } catch {}
      return;
    }
    
    try {
      // Helpers to map ids to labels for payload
      const countryLabel = countries.find(c => String(c.value) === String(values.country))?.label || '';
      const stateLabel = states.find(s => String(s.value) === String(values.state))?.label || '';
      const formatPhone = (p: string) => (p || '').replace(/\D/g, '');

      // Build MVC-like payload shape
      const description = `${entity} Donation for user: ${values.firstName} ${values.lastName} camp name: Camp amount  of : ${values.donationAmount}`;
      const appName = entity === 'HYC' ? 'flowery' : 'ykapps';
      const dataarray: any = {
        app: appName,
        entity: entity,
        paymentType: values.paymentMethod === 'ACH' ? 'ach' : 'cc',
        customerEmail: values.email,
        orderid: '0',
        amount: String(values.donationAmount),
        description,
        customer: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: formatPhone(values.phoneNo),
          company: 'string',
          notes: description,
          customerAccount: 'string',
          shippingSameAsBilling: true,
          billingAddress: {
            streetAddress1: values.address,
            streetAddress2: '-',
            city: values.city,
            stateCode: stateLabel,
            zipCode: values.postalCode,
            country: countryLabel,
          },
          shippingAddress: {
            streetAddress1: values.address,
            streetAddress2: '-',
            city: values.city,
            stateCode: stateLabel,
            zipCode: values.postalCode,
            country: countryLabel,
          },
        },
        creditcard: {
          creditCardNumber: values.cardNumber.replace(/\s/g, ''), // Strip spaces for API
          expirationDate: values.expirationDate,
          issuer: 'string',
          billingZipCode: values.postalCode,
          customerId: 'string',
          cvv: values.cvv,
        },
        ach: {
          isCheckingAccount: values.achAccountType === 'Checking' ? 'Y' : 'N',
          routingNumber: values.achRoutingNumber || 'string',
          accountNumber: values.achAccountNumber || 'string',
          bankName: values.achBankName || 'string',
          customerId: 'string',
          isDefault: 'true',
        },
        // Always include recurr block; will be populated for Recurring and kept as placeholders otherwise
        recurr: {
          paymentAmount: 'string',
          accountId: 'string',
          startDate: 'string',
          executionFrequencyType: 'string',
          executionFrequencyParameter: 'string',
          endDate: 'string',
          paymentSubType: 'string',
          invoiceNumber: 'string',
          firstPaymentAmount: 'string',
          firstPaymentDate: 'string',
          description: 'string',
        },
      };

      if (values.donationType === 'Recurring') {
        const now = new Date();
        dataarray.recurr.paymentAmount = String(values.donationAmount);
        dataarray.recurr.accountId = 'string';
        
        // For ACH recurring payments, Authorize.Net requires start date to be at least 1 day in the future
        let startDateToUse = new Date(values.recurringStartDate + 'T12:00:00');
        if (values.paymentMethod === 'ACH') {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          tomorrow.setHours(12, 0, 0, 0);
          if (startDateToUse <= new Date()) {
            startDateToUse = tomorrow;
          }
        }
        
        dataarray.recurr.startDate = startDateToUse.toISOString();
        dataarray.recurr.executionFrequencyType = 'Monthly';
        dataarray.recurr.executionFrequencyParameter = '1';
        
        // Calculate endDate and totalOccurrences based on subscriptionEndType
        let calculatedEndDate: string | null = null;
        let totalOccurrences: number | null = null;
        
        if (values.subscriptionEndType === 'NoEndDate') {
          // No end date - use max allowed by Authorize.Net (9999 occurrences)
          // Backend uses GetMonthDifference to calculate totalOccurrences from start/end dates
          // Use 9998 months to stay safely within 9999 limit (833 years + 2 months)
          totalOccurrences = 9998;
          const farFutureDate = new Date(startDateToUse);
          const yearsToAdd = 833; // 9998 / 12 = 833.16
          const monthsToAdd = 2;  // 9998 % 12 = 2
          farFutureDate.setFullYear(farFutureDate.getFullYear() + yearsToAdd);
          farFutureDate.setMonth(farFutureDate.getMonth() + monthsToAdd);
          calculatedEndDate = farFutureDate.toISOString();
        } else if (values.subscriptionEndType === 'EndsOnDate') {
          // Calculate occurrences from start to end date
          const endDate = new Date(values.recurringEndDate + 'T12:00:00');
          calculatedEndDate = endDate.toISOString();
          // Calculate months between start and end
          const startYear = startDateToUse.getFullYear();
          const startMonth = startDateToUse.getMonth();
          const endYear = endDate.getFullYear();
          const endMonth = endDate.getMonth();
          totalOccurrences = (endYear - startYear) * 12 + (endMonth - startMonth);
          if (totalOccurrences < 1) totalOccurrences = 1;
        } else if (values.subscriptionEndType === 'EndsAfterTerms') {
          // Calculate end date from number of terms
          totalOccurrences = parseInt(values.numberOfTerms, 10) || 12;
          const endDate = new Date(startDateToUse);
          endDate.setMonth(endDate.getMonth() + totalOccurrences);
          calculatedEndDate = endDate.toISOString();
        }
        
        // Always send endDate - backend uses GetMonthDifference to calculate totalOccurrences
        dataarray.recurr.endDate = calculatedEndDate;
        dataarray.recurr.paymentSubType = 'string';
        dataarray.recurr.invoiceNumber = 'string';
        dataarray.recurr.firstPaymentAmount = String(values.donationAmount);
        dataarray.recurr.firstPaymentDate = now.toISOString();
        dataarray.recurr.description = description;
      }

      // Route to correct payment API — entity field in payload selects HYC vs RMS AuthNet credentials
      const studentId: string = '0';
      const oneTimeFn = entity === 'HYC'
        ? donationService.authNetOneTimePaymentHYC
        : donationService.authNetOneTimePayment;
      const recurringFn = entity === 'HYC'
        ? donationService.authNetRecurringPaymentHYC
        : donationService.authNetRecurringPayment;
      const paymentResp: any = values.donationType === 'Recurring'
        ? await recurringFn(dataarray, studentId)
        : await oneTimeFn(dataarray);

      // Normalize success for both one-time and recurring responses
      const respMessages = (
        paymentResp?.messages ||
        paymentResp?.Messages ||
        paymentResp?.createTransactionResponse?.messages ||
        paymentResp?.message ||
        paymentResp?.Message ||
        {}
      );
      const resultCode = String(
        respMessages?.resultCode ?? paymentResp?.resultCode ?? ''
      );
      const msgArray = Array.isArray((respMessages as any)?.message)
        ? (respMessages as any).message
        : ((respMessages as any)?.message ? [(respMessages as any).message] : []);
      const messageCode = (msgArray[0]?.code) || (paymentResp?.createTransactionResponse?.messages?.message?.code);
      let messageText = '';
      if (typeof respMessages === 'string') {
        messageText = respMessages;
      } else {
        messageText = (msgArray[0]?.text || msgArray[0]?.description) || '';
      }
      // Fallbacks for top-level string messages
      if (!messageText && typeof (paymentResp as any)?.Message === 'string') {
        messageText = (paymentResp as any).Message;
      }
      if (!messageText && typeof (paymentResp as any)?.message === 'string') {
        messageText = (paymentResp as any).message;
      }
      const hasSubscriptionId = typeof paymentResp?.aRBCreateSubscriptionResponse?.subscriptionId === 'string' && paymentResp.aRBCreateSubscriptionResponse.subscriptionId.length > 0;
      const ok = (
        paymentResp?.success === true ||
        paymentResp?.isSuccess === true ||
        paymentResp?.status === 'OK' ||
        ['ok','OK','Ok'].includes(resultCode) ||
        messageCode === 'I00001' ||
        (values.donationType === 'Recurring' && hasSubscriptionId)
      );

      if (ok) {
        // Record donation in appropriate channel before sending email (non-blocking on failure)
        let addDonationStatus: any = null;
        try {
          const addDonationFn = entity === 'HYC'
            ? donationService.addHYCDonation
            : donationService.addDonation;
          const addResp: any = await addDonationFn({
            app: 'Donation',
            entity: entity,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            phoneNo: formatPhone(values.phoneNo),
            address: values.address,
            country: countryLabel,
            state: stateLabel,
            city: values.city,
            postalCode: values.postalCode,
            purposeOfDonation: values.purpose,
            paymentFor: values.paymentPurpose,
            donationAmount: Number(values.donationAmount || 0),
            donationType: values.donationType,
            paymentType: values.paymentMethod === 'ACH' ? 'ACH' : 'Credit Card',
          });
          const addMsg = (addResp?.message || addResp?.Message || 'Donation recorded successfully.');
          addDonationStatus = { success: true, message: addMsg };
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('AddDonation call failed (continuing to email):', (e as any)?.response?.data || e);
          }
          const data: any = (e as any)?.response?.data || (e as any)?.data || e;
          const addErrMsg = (data?.message || data?.Message || data?.error || (e as any)?.message || 'Unable to record donation.');
          addDonationStatus = { success: false, message: addErrMsg };
        }

        // Send confirmation email (non-blocking on failure)
        let emailStatus: any = null;
        try {
          // Build email data - include StartDate and EndDate for recurring donations
          const isRecurring = values.donationType === 'Recurring';
          const emailData: any = {
            Email: values.email,
            ContactNo: values.phoneNo,
            UserName: `${values.firstName} ${values.lastName}`.trim(),
            DonationDate: new Date().toISOString(),
            Amount: String(values.donationAmount),
            Address: `${values.address || ''} ${values.city || ''} ${stateLabel || ''} ${countryLabel || ''}`.replace(/\s+/g, ' ').trim(),
          };
          
          // Add recurring-specific fields
          if (isRecurring) {
            emailData.StartDate = formatMMDDYYYY(values.recurringStartDate);
            // Set EndDate text based on subscriptionEndType
            if (values.subscriptionEndType === 'NoEndDate') {
              emailData.EndDate = 'No End Date (Ongoing)';
            } else if (values.subscriptionEndType === 'EndsOnDate') {
              emailData.EndDate = formatMMDDYYYY(values.recurringEndDate);
            } else if (values.subscriptionEndType === 'EndsAfterTerms') {
              // Calculate the actual end date for display
              const startDate = new Date(values.recurringStartDate + 'T12:00:00');
              const numTerms = parseInt(values.numberOfTerms, 10) || 12;
              const calcEndDate = new Date(startDate);
              calcEndDate.setMonth(calcEndDate.getMonth() + numTerms);
              emailData.EndDate = `${formatMMDDYYYY(calcEndDate.toISOString().slice(0, 10))} (${numTerms} payments)`;
            }
          }
          
          // Use different template for recurring donations (entity-aware)
          const recurringTemplate = entity === 'HYC' ? 'HYCRecurringDonation' : 'RMSRecurringDonation';
          const templateToUse = isRecurring ? recurringTemplate : emailTemplateName;
          
          const emailResp: any = await donationService.sendEmailMessage(
            emailData,
            { appName: emailAppName, templateName: templateToUse, orgId: 3 }
          );
          const emOk = emailResp?.success !== false; // treat as success unless explicitly false
          const emMsg = emailResp?.message || emailResp?.Message || (emOk ? 'A confirmation email was sent.' : 'Failed to send confirmation email.');
          let rurl: string = '';
          if (typeof emailResp === 'string') {
            rurl = emailResp; // API returned the PDF URL directly
          } else {
            rurl = (
              emailResp?.receiptUrl || emailResp?.ReceiptUrl || emailResp?.receiptURL ||
              emailResp?.attachment || emailResp?.Attachment ||
              emailResp?.attachmentUrl || emailResp?.AttachmentUrl || emailResp?.attachmentURL ||
              emailResp?.pdfUrl || emailResp?.pdfURL ||
              emailResp?.fileUrl || emailResp?.fileURL ||
              emailResp?.url || emailResp?.Url || emailResp?.URL ||
              emailResp?.Response || emailResp?.response ||
              // nested under data
              emailResp?.data?.receiptUrl || emailResp?.data?.ReceiptUrl || emailResp?.data?.receiptURL ||
              emailResp?.data?.attachment || emailResp?.data?.Attachment ||
              emailResp?.data?.attachmentUrl || emailResp?.data?.AttachmentUrl || emailResp?.data?.attachmentURL ||
              emailResp?.data?.pdfUrl || emailResp?.data?.pdfURL ||
              emailResp?.data?.fileUrl || emailResp?.data?.fileURL ||
              emailResp?.data?.url || emailResp?.data?.Url || emailResp?.data?.URL ||
              emailResp?.data?.Response || emailResp?.data?.response ||
              // sometimes wrapped deeper
              emailResp?.Response?.attachmentUrl || emailResp?.Response?.AttachmentUrl || emailResp?.Response?.url ||
              emailResp?.response?.attachmentUrl || emailResp?.response?.AttachmentUrl || emailResp?.response?.url ||
              ''
            );
          }
          emailStatus = { success: emOk, message: emMsg, receiptUrl: rurl };
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('Email send failed; continuing with success UI.', (e as any)?.response?.data || e);
          }
          const data: any = (e as any)?.response?.data || (e as any)?.data || e;
          const emMsg = (data?.message || data?.Message || (e as any)?.message || 'Failed to send confirmation email.');
          emailStatus = { success: false, message: emMsg };
        }

        // SweetAlert2 success popup - DISABLED
        // if (typeof window !== 'undefined') {
        //   const extra = (values.donationType === 'Recurring' && hasSubscriptionId)
        //     ? ` Subscription ID: ${paymentResp.aRBCreateSubscriptionResponse.subscriptionId}.`
        //     : '';
        //   Swal.fire({
        //     icon: 'success',
        //     title: 'Thank you!',
        //     text: `Your donation was successful and a confirmation email has been sent.${extra}`,
        //     confirmButtonColor: '#28a745',
        //     confirmButtonText: 'Great',
        //     customClass: { popup: 'swal2-modern-popup' }
        //   });
        // }

        let successMsg = 'Donation successfully processed. Thank you!';
        if (values.donationType === 'Recurring' && hasSubscriptionId) {
          const subscriptionId = paymentResp.aRBCreateSubscriptionResponse.subscriptionId;
          const startDateFormatted = formatMMDDYYYY(values.recurringStartDate);
          let endDateText = '';
          if (values.subscriptionEndType === 'NoEndDate') {
            endDateText = 'will continue indefinitely';
          } else if (values.subscriptionEndType === 'EndsOnDate') {
            endDateText = `will continue until ${formatMMDDYYYY(values.recurringEndDate)}`;
          } else if (values.subscriptionEndType === 'EndsAfterTerms') {
            endDateText = `will continue for ${values.numberOfTerms} monthly payments`;
          }
          successMsg = `Recurring donation successfully created. Subscription ID: ${subscriptionId}. Your monthly donation of $${values.donationAmount} will start on ${startDateFormatted} and ${endDateText}.`;
        }
        setSubmissionStatus({ success: true, message: successMsg });
        // Reset the form to initial values
        if (formikHelpers && typeof formikHelpers.resetForm === 'function') {
          formikHelpers.resetForm();
        }
        // Reset reCAPTCHA after successful submission
        try {
          const w = window as any;
          if (recaptchaWidgetId !== null && w.grecaptcha) {
            w.grecaptcha.reset(recaptchaWidgetId);
            setRecaptchaToken('');
          }
        } catch {}

        // Persist grouped results and navigate to Success page
        try {
          if (typeof window !== 'undefined') {
            const donorName = `${values.firstName} ${values.lastName}`.trim();
            const flowResults = {
              originPath: window.location.pathname,
              donor: { name: donorName, email: values.email, amount: String(values.donationAmount), donationType: values.donationType },
              payment: { success: true, message: successMsg },
              addDonation: addDonationStatus,
              email: emailStatus,
            } as any;
            window.sessionStorage.setItem('donationFlowResults', JSON.stringify(flowResults));
            window.location.href = '/success';
          }
        } catch {}
      } else {
        // Handle specific error messages with user-friendly alternatives
        let errMsg = messageText || 'Failed to process donation. Please verify your details and try again.';
        
        // Handle "record cannot be found" error - typically means payment profile creation failed
        if (errMsg.toLowerCase().includes('record cannot be found') || errMsg.toLowerCase().includes('record not found')) {
          errMsg = 'Unable to process your payment method. Please verify your card details are correct and try again. If the issue persists, please try a different card.';
        }
        
        setSubmissionStatus({ success: false, message: errMsg });

        // Persist failed payment and navigate to Success page
        try {
          if (typeof window !== 'undefined') {
            const donorName = `${values.firstName} ${values.lastName}`.trim();
            const flowResults = {
              originPath: window.location.pathname,
              donor: { name: donorName, email: values.email, amount: String(values.donationAmount), donationType: values.donationType },
              payment: { success: false, message: errMsg },
              addDonation: null,
              email: null,
            } as any;
            window.sessionStorage.setItem('donationFlowResults', JSON.stringify(flowResults));
            window.location.href = '/success';
          }
        } catch {}
      }
    } catch (error: any) {
      // Try to surface a useful error message from API response (Axios error shapes)
      let apiMessage = 'An error occurred while processing your donation. Please try again later.';
      try {
        const data = error?.response?.data ?? error?.data ?? null;
        if (data) {
          // Common message containers
          const msgs = (data as any).messages
            || (data as any).Messages
            || (data as any).message
            || (data as any).Message
            || (data as any)?.createTransactionResponse?.messages
            || (data as any)?.aRBCreateSubscriptionResponse?.messages;

          // Meta.errors.errorMessages structure (e.g., Authorize.Net exception wrapper)
          try {
            const metaErrors = (data as any)?.meta?.errors?.errorMessages;
            if (Array.isArray(metaErrors) && metaErrors.length) {
              const first = metaErrors[0] || {};
              const metaMsg = first.message || first.description || first.text || first.error || '';
              if (metaMsg) {
                apiMessage = String(metaMsg);
              }
            }
          } catch {}

          if (typeof msgs === 'string') {
            apiMessage = msgs;
          } else if ((msgs as any)?.message || (msgs as any)?.Message) {
            const inner = (msgs as any).message ?? (msgs as any).Message;
            const arr = Array.isArray(inner) ? inner : [inner];
            const first = arr[0] || {};
            apiMessage = first.text || first.description || first.message || first.Message || apiMessage;
          }

          // Authorize.Net specific error block
          const anetErr = (data as any)?.createTransactionResponse?.errors?.error;
          if (!apiMessage && anetErr) {
            const arr = Array.isArray(anetErr) ? anetErr : [anetErr];
            apiMessage = arr[0]?.errorText || arr[0]?.text || apiMessage;
          }

          // ARB subscription specific messages
          const arbMsg = (data as any)?.aRBCreateSubscriptionResponse?.messages?.message;
          if (!apiMessage && arbMsg) {
            const arr = Array.isArray(arbMsg) ? arbMsg : [arbMsg];
            apiMessage = arr[0]?.text || arr[0]?.description || apiMessage;
          }

          // Generic errors array
          if (!apiMessage && Array.isArray((data as any)?.errors) && (data as any).errors.length) {
            const e0 = (data as any).errors[0];
            apiMessage = (e0 && (e0.message || e0.error || (typeof e0 === 'string' ? e0 : ''))) || apiMessage;
          }

          // Capitalized or lowercase top-level Message fields
          if (!apiMessage && typeof (data as any)?.Message === 'string') {
            apiMessage = (data as any).Message;
          }
          if (!apiMessage && typeof (data as any)?.message === 'string') {
            apiMessage = (data as any).message;
          }

          // Plain string response body
          if (!apiMessage && typeof data === 'string') {
            apiMessage = data;
          }
        } else if (typeof error?.message === 'string') {
          apiMessage = error.message;
        }
      } catch {}

      setSubmissionStatus({ success: false, message: apiMessage });
      // Persist fatal error and navigate to Success page
      try {
        if (typeof window !== 'undefined') {
          const donorName = `${values.firstName} ${values.lastName}`.trim();
          const flowResults = {
            originPath: window.location.pathname,
            donor: { name: donorName, email: values.email, amount: String(values.donationAmount), donationType: values.donationType },
            payment: { success: false, message: apiMessage },
            addDonation: null,
            email: null,
          } as any;
          window.sessionStorage.setItem('donationFlowResults', JSON.stringify(flowResults));
          window.location.href = '/success';
        }
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="donation-form-container">
      {/* Brand header with logo and styled title */}
      <div className="brand-header">
        {brandLogoSrc ? (
          <img src={brandLogoSrc} alt={brandAlt} className="brand-logo" />
        ) : null}
        <h1 className="brand-title">
          {entity === 'HYC' ? (title || 'Donation for Harmony Youth Center') : (title || 'Donation for Radha Madhav Society')}
        </h1>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formikProps) => (
          <Form>
            <Row>
              {/* Personal Details Card */}
              <Col md={12}>
                <Card className="mb-4 elevated-card">
                  <Card.Body>
                    <div className="section-surface surface-personal">
                      {/* Personal Details in exactly 3 rows */}
                    <Row className="mb-3">
                      <Col md={4}>
                        <label htmlFor="firstName">First Name *</label>
                        <Field name="firstName" type="text" className={`form-control ${formikProps.touched.firstName && formikProps.errors.firstName ? 'is-invalid' : ''}`} placeholder="First Name" maxLength={50} />
                        <ErrorMessage name="firstName" component="div" className="text-danger" />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="lastName">Last Name *</label>
                        <Field name="lastName" type="text" className={`form-control ${formikProps.touched.lastName && formikProps.errors.lastName ? 'is-invalid' : ''}`} placeholder="Last Name" maxLength={50} />
                        <ErrorMessage name="lastName" component="div" className="text-danger" />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="email">Email *</label>
                        <Field name="email" type="email" className={`form-control ${formikProps.touched.email && formikProps.errors.email ? 'is-invalid' : ''}`} placeholder="Email" />
                        <ErrorMessage name="email" component="div" className="text-danger" />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={4}>
                        <label htmlFor="phoneNo">Phone No *</label>
                        <Field name="phoneNo" type="text" className={`form-control ${formikProps.touched.phoneNo && formikProps.errors.phoneNo ? 'is-invalid' : ''}`} placeholder="Phone No" />
                        <ErrorMessage name="phoneNo" component="div" className="text-danger" />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="address">Address *</label>
                        <Field name="address" as="textarea" className={`form-control ${formikProps.touched.address && formikProps.errors.address ? 'is-invalid' : ''}`} placeholder="Address" maxLength={60} />
                        <ErrorMessage name="address" component="div" className="text-danger" />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="country">Select Country *</label>
                        <Select
                          options={countries.map(country => ({ value: country.value, label: country.label, id: country.id }))}
                          value={(formikProps.values.country && countries.length) ? { value: formikProps.values.country, label: countries.find(c => c.value === formikProps.values.country)?.label || '' } : null}
                          onChange={(option) => handleCountryChange(option, formikProps)}
                          className={`basic-select ${formikProps.touched.country && formikProps.errors.country ? 'is-invalid' : ''}`}
                          classNamePrefix="select"
                          placeholder="Canada"
                          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                          menuPosition="fixed"
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 })
                          }}
                        />
                        <ErrorMessage name="country" component="div" className="text-danger" />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={4}>
                        <label htmlFor="state">Select State *</label>
                        <Select
                          options={states.map(s => ({ value: s.value, label: s.label }))}
                          value={formikProps.values.state ? { value: formikProps.values.state, label: states.find(s => s.value === formikProps.values.state)?.label || '' } : null}
                          onChange={(option) => { 
                            if (option) { 
                              formikProps.setFieldValue('state', option.value); 
                            } 
                          }}
                          isDisabled={states.length === 0}
                          className={`basic-select ${formikProps.touched.state && formikProps.errors.state ? 'is-invalid' : ''}`}
                          classNamePrefix="select"
                          placeholder="Select State"
                          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                          menuPosition="fixed"
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 })
                          }}
                        />
                        <ErrorMessage name="state" component="div" className="text-danger" />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="city">City *</label>
                        <Field name="city" type="text" className={`form-control ${formikProps.touched.city && formikProps.errors.city ? 'is-invalid' : ''}`} placeholder="City" maxLength={40} />
                        <ErrorMessage name="city" component="div" className="text-danger" />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="postalCode">Postal Code/ZIP Code *</label>
                        <Field name="postalCode" type="text" className={`form-control ${formikProps.touched.postalCode && formikProps.errors.postalCode ? 'is-invalid' : ''}`} placeholder="Postal Code/ZIP Code" />
                        <ErrorMessage name="postalCode" component="div" className="text-danger" />
                      </Col>
                    </Row>
                    </div>

                    <div className="section-surface surface-payment">
                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="paymentPurpose">This payment is for *</label>
                        <Select
                          options={donationTypes}
                          value={formikProps.values.paymentPurpose ? { value: formikProps.values.paymentPurpose, label: formikProps.values.paymentPurpose } : null}
                          onChange={(option) => { if (option) { formikProps.setFieldValue('paymentPurpose', option.value); } }}
                          className={`basic-select ${formikProps.touched.paymentPurpose && formikProps.errors.paymentPurpose ? 'is-invalid' : ''}`}
                          classNamePrefix="select"
                          placeholder="Search & Select Donation for"
                          isLoading={donationTypes.length === 0}
                        />
                        <ErrorMessage name="paymentPurpose" component="div" className="text-danger" />
                      </Col>
                      <Col md={6}>
                        <label htmlFor="purpose">This payment for (comment)</label>
                        <Field name="purpose" type="text" className="form-control" placeholder="Add a short comment (optional)" />
                      </Col>
                    </Row>
                    
                    <Row className="mb-3">
                      <Col md={4}>
                        <label htmlFor="donationAmount">Donation Amount *</label>
                        <Field name="donationAmount">
                          {({ field }: any) => (
                            <input
                              {...field}
                              type="text"
                              inputMode="decimal"
                              className={`form-control ${formikProps.touched.donationAmount && formikProps.errors.donationAmount ? 'is-invalid' : ''}`}
                              placeholder="Donation Amount"
                              onChange={(e) => {
                                let v = e.target.value;
                                v = v.replace(/[^0-9.]/g, '');
                                const parts = v.split('.');
                                if (parts.length > 2) { v = parts[0] + '.' + parts.slice(1).join(''); }
                                const [intp, decp = ''] = v.split('.');
                                const dec2 = decp.slice(0, 2);
                                v = decp.length ? `${intp}.${dec2}` : intp;
                                formikProps.setFieldValue('donationAmount', v);
                              }}
                            />
                          )}
                        </Field>
                        <ErrorMessage name="donationAmount" component="div" className="text-danger" />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="donationType">Select Donation Type *</label>
                        <Select
                          options={[
                            { value: 'OneTime', label: 'OneTime' },
                            { value: 'Recurring', label: 'Recurring' }
                          ]}
                          onChange={(option) => {
                            if (option) {
                              formikProps.setFieldValue('donationType', option.value);
                            }
                          }}
                          value={{ value: formikProps.values.donationType, label: formikProps.values.donationType }}
                          className={`basic-select theme-a ${formikProps.touched.donationType && formikProps.errors.donationType ? 'is-invalid' : ''}`}
                          classNamePrefix="select"
                        />
                        <ErrorMessage name="donationType" component="div" className="text-danger" />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="paymentMethod">Select Payment Method *</label>
                        <Select
                          options={[
                            { value: 'CreditCard', label: 'Credit Card' },
                            { value: 'ACH', label: 'ACH' }
                          ]}
                          onChange={(option) => {
                            if (option) {
                              formikProps.setFieldValue('paymentMethod', option.value);
                            }
                          }}
                          value={{
                            value: formikProps.values.paymentMethod,
                            label: formikProps.values.paymentMethod === 'ACH' ? 'ACH' : 'Credit Card',
                          }}
                          className={`basic-select theme-b ${formikProps.touched.paymentMethod && formikProps.errors.paymentMethod ? 'is-invalid' : ''}`}
                          classNamePrefix="select"
                        />
                      </Col>
                    </Row>
                    {formikProps.values.donationType === 'Recurring' && (
                      <>
                        <Row className="mb-3">
                          <Col md={6}>
                            <label htmlFor="recurringStartDate">Start Date *</label>
                            <Field name="recurringStartDate">
                              {({ field }: any) => (
                                <input
                                  {...field}
                                  type="date"
                                  id="recurringStartDate"
                                  className={`form-control date-highlight ${formikProps.touched.recurringStartDate && formikProps.errors.recurringStartDate ? 'is-invalid' : ''}`}
                                  min={todayStr}
                                  required
                                  aria-invalid={Boolean(formikProps.touched.recurringStartDate && formikProps.errors.recurringStartDate)}
                                  aria-describedby="recurringStartDate-error"
                                  inputMode="none"
                                  onKeyDown={(e) => {
                                    const allowed = ['Tab', 'Shift', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Escape', 'Enter'];
                                    if (!allowed.includes(e.key)) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onPaste={(e) => e.preventDefault()}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    formikProps.setFieldValue('recurringStartDate', v);
                                  }}
                                />
                              )}
                            </Field>
                            <ErrorMessage name="recurringStartDate" component="div" className="error-modern" id="recurringStartDate-error" />
                          </Col>
                          <Col md={6}>
                            <label htmlFor="subscriptionEndType">Subscription End *</label>
                            <Select
                              options={[
                                { value: 'NoEndDate', label: 'No End Date' },
                                { value: 'EndsOnDate', label: 'Ends On This Date' },
                                { value: 'EndsAfterTerms', label: 'Ends After (Number of Terms)' }
                              ]}
                              onChange={(option) => {
                                if (option) {
                                  formikProps.setFieldValue('subscriptionEndType', option.value);
                                  // Reset dependent fields when changing type
                                  if (option.value === 'NoEndDate') {
                                    formikProps.setFieldValue('recurringEndDate', '');
                                    formikProps.setFieldValue('numberOfTerms', '');
                                  } else if (option.value === 'EndsOnDate') {
                                    formikProps.setFieldValue('numberOfTerms', '');
                                    // Set default end date to 1 year from start
                                    const start = formikProps.values.recurringStartDate;
                                    if (start) {
                                      const d = new Date(start + 'T12:00:00');
                                      d.setFullYear(d.getFullYear() + 1);
                                      formikProps.setFieldValue('recurringEndDate', d.toISOString().slice(0, 10));
                                    }
                                  } else if (option.value === 'EndsAfterTerms') {
                                    formikProps.setFieldValue('recurringEndDate', '');
                                    formikProps.setFieldValue('numberOfTerms', '12');
                                  }
                                }
                              }}
                              value={{
                                value: formikProps.values.subscriptionEndType,
                                label: formikProps.values.subscriptionEndType === 'NoEndDate' ? 'No End Date' 
                                  : formikProps.values.subscriptionEndType === 'EndsOnDate' ? 'Ends On This Date'
                                  : 'Ends After (Number of Terms)'
                              }}
                              className={`basic-select ${formikProps.touched.subscriptionEndType && formikProps.errors.subscriptionEndType ? 'is-invalid' : ''}`}
                              classNamePrefix="select"
                            />
                            <ErrorMessage name="subscriptionEndType" component="div" className="text-danger" />
                          </Col>
                        </Row>
                        
                        {/* Show End Date field only when "Ends On This Date" is selected */}
                        {formikProps.values.subscriptionEndType === 'EndsOnDate' && (
                          <Row className="mb-3">
                            <Col md={6}>
                              <label htmlFor="recurringEndDate">End Date *</label>
                              <Field name="recurringEndDate">
                                {({ field }: any) => (
                                  <input
                                    {...field}
                                    type="date"
                                    id="recurringEndDate"
                                    className={`form-control date-highlight ${formikProps.touched.recurringEndDate && formikProps.errors.recurringEndDate ? 'is-invalid' : ''}`}
                                    min={(() => {
                                      const s = formikProps.values.recurringStartDate;
                                      if (!s) return todayStr;
                                      const d = new Date(s + 'T12:00:00');
                                      d.setMonth(d.getMonth() + 2); // At least 2 months after start
                                      return d.toISOString().slice(0, 10);
                                    })()}
                                    required
                                    aria-invalid={Boolean(formikProps.touched.recurringEndDate && formikProps.errors.recurringEndDate)}
                                    aria-describedby="recurringEndDate-error"
                                    inputMode="none"
                                    onKeyDown={(e) => {
                                      const allowed = ['Tab', 'Shift', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Escape', 'Enter'];
                                      if (!allowed.includes(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    onPaste={(e) => e.preventDefault()}
                                  />
                                )}
                              </Field>
                              <ErrorMessage name="recurringEndDate" component="div" className="error-modern" id="recurringEndDate-error" />
                            </Col>
                          </Row>
                        )}
                        
                        {/* Show Number of Terms field only when "Ends After" is selected */}
                        {formikProps.values.subscriptionEndType === 'EndsAfterTerms' && (
                          <Row className="mb-3">
                            <Col md={6}>
                              <label htmlFor="numberOfTerms">Number of Terms (Months) *</label>
                              <Field name="numberOfTerms">
                                {({ field }: any) => (
                                  <input
                                    {...field}
                                    type="number"
                                    id="numberOfTerms"
                                    className={`form-control ${formikProps.touched.numberOfTerms && formikProps.errors.numberOfTerms ? 'is-invalid' : ''}`}
                                    min="1"
                                    max="9999"
                                    placeholder="Enter number of monthly payments"
                                  />
                                )}
                              </Field>
                              <ErrorMessage name="numberOfTerms" component="div" className="text-danger" />
                            </Col>
                          </Row>
                        )}
                      </>
                    )}

                    {/* Credit Card fields */}
                    {formikProps.values.paymentMethod === 'CreditCard' && (
                      <>
                        <Row className="mb-3">
                          <Col md={12}>
                            <label htmlFor="cardNumber">Card Number *</label>
                            <Field name="cardNumber">
                              {({ field }: any) => (
                                <input
                                  {...field}
                                  type="text"
                                  className="form-control"
                                  placeholder="4444 4444 4444 4444"
                                  inputMode="numeric"
                                  maxLength={23}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/\s/g, '').replace(/\D/g, '').slice(0, 19);
                                    const formatted = value.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
                                    formikProps.setFieldValue('cardNumber', formatted);
                                  }}
                                />
                              )}
                            </Field>
                            <ErrorMessage name="cardNumber" component="div" className="text-danger" />
                          </Col>
                        </Row>
                        
                        <Row className="mb-3">
                          <Col md={6}>
                            <label htmlFor="cvv">CVV/CVC *</label>
                            <Field 
                              name="cvv"
                              type="password"
                              className="form-control"
                              placeholder="CVV"
                              maxLength={4}
                            />
                            <ErrorMessage name="cvv" component="div" className="text-danger" />
                          </Col>
                          <Col md={6}>
                            <label htmlFor="expirationDate">Expiration Date (MM/YY) *</label>
                            <Field
                              name="expirationDate"
                              type="text"
                              className="form-control"
                              placeholder="MM/YY"
                              maxLength={5}
                              onChange={(e: any) => {
                                let value = e.target.value.replace(/\D/g, '');
                                if (value.length >= 2) {
                                  value = value.slice(0, 2) + '/' + value.slice(2, 4);
                                }
                                formikProps.setFieldValue('expirationDate', value);
                              }}
                            />
                            <ErrorMessage name="expirationDate" component="div" className="text-danger" />
                          </Col>
                        </Row>
                      </>
                    )}
                    
                    {/* ACH fields */}
                    {formikProps.values.paymentMethod === 'ACH' && (
                      <>                       
                        <Row className="mb-3">
                          <Col md={6}>
                            <label htmlFor="achRoutingNumber">Routing Number *</label>
                            <Field 
                              name="achRoutingNumber"
                              type="text"
                              className="form-control"
                              placeholder="Enter Routing Number"
                              inputMode="numeric"
                              maxLength={9}
                            />
                            <ErrorMessage name="achRoutingNumber" component="div" className="text-danger" />
                          </Col>
                          <Col md={6}>
                            <label htmlFor="achAccountNumber">Account Number *</label>
                            <Field 
                              name="achAccountNumber"
                              type="text"
                              className="form-control"
                              placeholder="Enter Account Number"
                              inputMode="numeric"
                            />
                            <ErrorMessage name="achAccountNumber" component="div" className="text-danger" />
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md={6}>
                            <label htmlFor="achBankName">Bank Name *</label>
                            <Field 
                              name="achBankName"
                              type="text"
                              className="form-control"
                              placeholder="Enter Bank Name"
                            />
                            <ErrorMessage name="achBankName" component="div" className="text-danger" />
                          </Col>
                          <Col md={6}>
                            <label htmlFor="achAccountType">Account Type *</label>
                            <Field as="select" name="achAccountType" className="form-control">
                              <option value="Checking">Checking</option>
                              <option value="Savings">Savings</option>
                            </Field>
                            <ErrorMessage name="achAccountType" component="div" className="text-danger" />
                          </Col>
                        </Row>
                      </>
                    )}
                    
                    {/* Google reCAPTCHA + Submit on the same row */}
                    <Row className="align-items-center mb-3">
                      <Col md={8}>
                        <div id="recaptcha-container"></div>
                        {!recaptchaToken && (<div className="text-danger mt-2">Please complete reCAPTCHA</div>)}
                      </Col>
                      <Col md={4} className="text-md-end text-center">
                        <Button type="submit" variant="success" disabled={loading || !recaptchaToken} className="btn-submit btn-lg shadow-sm">
                          {loading ? (
                            'Processing...'
                          ) : (
                            <>
                              <span className="btn-icon" aria-hidden="true">
                                <svg className="donate-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">
                                  <path d="M12 .587l3.668 7.431 8.2 1.192-5.934 5.787 1.401 8.168L12 18.896 4.665 23.165l1.401-8.168L.132 9.21l8.2-1.192L12 .587z" />
                                </svg>
                              </span>
                              Donate
                            </>
                          )}
                        </Button>
                        {loading && (
                          <div className="mt-2 d-flex align-items-center justify-content-md-end justify-content-center">
                            <Spinner animation="border" role="status" size="sm" className="me-2"><span className="visually-hidden">Processing...</span></Spinner>
                            <span>Please wait...</span>
                          </div>
                        )}
                      </Col>
                    </Row>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
      {loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(240, 244, 255, 0.98) 100%)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: 24,
            backdropFilter: 'blur(2px)',
          }}
          aria-live="polite"
          aria-busy="true"
          role="alert"
        >
          <div
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
              borderRadius: 24,
              padding: '60px 48px',
              boxShadow: '0 20px 60px rgba(99, 102, 241, 0.25), 0 8px 24px rgba(99, 102, 241, 0.15)',
              border: '2px solid rgba(99, 102, 241, 0.15)',
              textAlign: 'center',
              maxWidth: 500,
              animation: 'fadeIn 0.4s ease-out',
            }}
          >
            <img
              src={brandLogoSrc}
              alt={brandAlt}
              style={{
                width: 140,
                height: 'auto',
                marginBottom: 28,
                opacity: 0.95,
                filter: 'drop-shadow(0 4px 12px rgba(99, 102, 241, 0.2))',
              }}
              onError={(e) => { (e.currentTarget as HTMLImageElement).src = DEFAULT_LOGO; }}
            />
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 24,
              position: 'relative',
              width: '100%',
            }}>
              <div style={{
                position: 'absolute',
                width: '110px',
                height: '110px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(236, 72, 153, 0.06) 100%)',
                border: '2px solid rgba(99, 102, 241, 0.2)',
                boxShadow: '0 0 35px rgba(99, 102, 241, 0.22), inset 0 0 18px rgba(99, 102, 241, 0.12)',
                animation: 'spinPulse 3s ease-in-out infinite',
              }} />
              <Spinner
                animation="border"
                role="status"
                variant="success"
                style={{
                  width: '4.2rem',
                  height: '4.2rem',
                  color: '#6366f1',
                  filter: 'drop-shadow(0 3px 12px rgba(99, 102, 241, 0.35))',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <span className="visually-hidden">Processing...</span>
              </Spinner>
            </div>
            <div style={{
              fontSize: 20,
              color: '#1f2937',
              marginBottom: 12,
              fontWeight: 700,
              letterSpacing: '0.3px',
            }}>
              {brandAlt}
            </div>
            <div style={{
              fontSize: 24,
              color: '#6366f1',
              fontWeight: 800,
              marginBottom: 16,
              letterSpacing: '-0.02em',
              background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 50%, #f59e0b 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              Processing your donation...
            </div>
            <div style={{
              marginTop: 16,
              color: '#6b7280',
              fontSize: 15,
              fontWeight: 500,
              letterSpacing: '0.3px',
            }}>
              Please do not close this window.
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default DonationForm;
